import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { QA_REPORT_ACTIONS } from '../../../../types';
import { t } from 'i18next';
import utils, { toggleValueInArray } from 'utils';
import useCallbackRef from 'hooks/useCallbackRef';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import FilterActionWrapper from 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/filters/FilterActionWrapper';
import { Button, Checkbox } from 'ncoded-component-library';
import RefreshIcon from 'icons/Refresh.icon';
import { NAVIGATION_VARIANTS, NavigationVariants } from 'models/Navigation';
import useQueryFilter from 'hooks/useQueryFilter';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';

import './QaFilter.styles.scss';
import './QaFilter.styles.responsive.scss';

type QAFilterProps = {
  className?: string;
  variant?: NavigationVariants;
  renderAsPortal?: boolean;
};

const FILTER_KEY = 'qaAction';

const QAFilter: React.FC<QAFilterProps> = (props) => {
  const { className, variant = NAVIGATION_VARIANTS.SECONDARY, renderAsPortal } =
    props;

  const createSelectOptions = useCreateSelectOptions();

  const actionOptions = useMemo(
    () => createSelectOptions(Object.values(QA_REPORT_ACTIONS)),
    [createSelectOptions],
  );

  const {
    params: { qaAction },
    onFiltersApply,
  } = useQueryFilter();

  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>(null);
  const defaultVal = useMemo(
    () => (qaAction ? utils.mapParamsToArray(qaAction) : []),
    [qaAction],
  );
  const [currentState, setCurrentState] = useState(defaultVal);

  const classes = classNames('qa-filter', className);

  const onSelectFilters = useCallback(
    (options: string[]) => {
      dropdown.setIsOpen(false);

      const patchedVal = utils.multiSelectValueToStringArray(options as any);

      onFiltersApply(FILTER_KEY, patchedVal);
    },
    [dropdown, onFiltersApply],
  );

  const triggerTitle = useMemo(
    () =>
      qaAction
        ? defaultVal?.length < 2
          ? t(utils.stringifyFilterValues(qaAction))
          : t('openToSeeQAFilters')
        : t('qaAction'),
    [defaultVal, qaAction],
  );

  useEffect(() => {
    setCurrentState(defaultVal);
  }, [defaultVal]);

  return (
    <FilterActionWrapper
      ref={dropdownRef}
      className={classes}
      trigerTitle={triggerTitle}
      icon={RefreshIcon}
      variant={variant}
      renderAsPortal={renderAsPortal}
      portalClassName="reports-filters__portal"
      onClose={() => {
        setCurrentState(defaultVal);
      }}
    >
      <div>
        {actionOptions.map(({ value, label }) => (
          <Checkbox
            key={value}
            label={t(label)}
            checked={currentState.some((el) => el === value)}
            onChange={() => {
              setCurrentState((prev) => toggleValueInArray(prev, value));
            }}
          />
        ))}
      </div>

      <div className="filter-action-wrapper__buttons">
        <Button variant="link" onClick={() => dropdown?.setIsOpen(false)}>
          {t('cancel')}
        </Button>
        <Button
          variant="outline"
          disabled={currentState === defaultVal}
          onClick={() => onSelectFilters(currentState)}
        >
          {t('applyQAFilter')}
        </Button>
      </div>
    </FilterActionWrapper>
  );
};

export default QAFilter;
